/* ============================================================
   formulaEngine — formula 속성용 자체 수식 파서/평가기
   - eval / new Function 절대 사용 안 함 (토크나이저 + 재귀 하강 파서)
   - 지원 문법:
     · prop("속성명") 참조
     · 사칙연산 + - * / %  (+ 는 문자열 연결 겸용)
     · 비교 == != > < >= <=  (= 는 == 의 별칭)
     · 논리 and / or / not  (&& || ! 별칭 허용)
     · 리터럴: 숫자, 문자열("…" '…'), true/false
     · 함수: if(cond, a, b), concat(...), length(x), round(x[, 자릿수]),
             abs(x), empty(x), now(), dateBetween(d1, d2, "days"),
             format(x)
   - 결과는 렌더 시 계산 (저장하지 않음). 오류는 throw → 호출부에서 "#오류" 처리.
   ============================================================ */

/* ---------------- 토크나이저 ---------------- */
function tokenize(src) {
  const toks = [];
  let i = 0;
  const isDigit = (c) => c >= '0' && c <= '9';
  const isIdStart = (c) => /[A-Za-z_가-힣]/.test(c);
  const isIdChar = (c) => /[A-Za-z0-9_가-힣]/.test(c);

  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }

    // 숫자 (소수 포함)
    if (isDigit(c) || (c === '.' && isDigit(src[i + 1]))) {
      let j = i;
      while (j < src.length && (isDigit(src[j]) || src[j] === '.')) j++;
      const raw = src.slice(i, j);
      const v = parseFloat(raw);
      if (Number.isNaN(v)) throw new Error(`숫자를 해석할 수 없습니다: ${raw}`);
      toks.push({ t: 'num', v });
      i = j;
      continue;
    }

    // 문자열 ("…" / '…', \ 이스케이프)
    if (c === '"' || c === "'") {
      const q = c;
      let j = i + 1;
      let s = '';
      while (j < src.length && src[j] !== q) {
        if (src[j] === '\\' && j + 1 < src.length) { s += src[j + 1]; j += 2; }
        else { s += src[j]; j++; }
      }
      if (j >= src.length) throw new Error('문자열이 닫히지 않았습니다');
      toks.push({ t: 'str', v: s });
      i = j + 1;
      continue;
    }

    // 식별자 / 키워드 (함수명, and/or/not, true/false)
    if (isIdStart(c)) {
      let j = i;
      while (j < src.length && isIdChar(src[j])) j++;
      toks.push({ t: 'id', v: src.slice(i, j) });
      i = j;
      continue;
    }

    // 2글자 연산자 우선
    const two = src.slice(i, i + 2);
    if (['==', '!=', '>=', '<=', '&&', '||'].includes(two)) {
      toks.push({ t: 'op', v: two });
      i += 2;
      continue;
    }
    if ('+-*/%()<>,=!'.includes(c)) {
      toks.push({ t: 'op', v: c });
      i++;
      continue;
    }
    throw new Error(`알 수 없는 문자: ${c}`);
  }
  return toks;
}

/* ---------------- 파서 (재귀 하강) ----------------
   우선순위: or < and < not < 비교 < 덧셈 < 곱셈 < 단항- < primary */
export function parseFormula(src) {
  const toks = tokenize(String(src || ''));
  let p = 0;
  const peek = () => toks[p];
  const next = () => toks[p++];
  const isOp = (t, v) => t && t.t === 'op' && t.v === v;
  const expectOp = (v) => {
    const t = next();
    if (!isOp(t, v)) throw new Error(`'${v}' 가 필요합니다`);
  };

  function parseOr() {
    let l = parseAnd();
    for (;;) {
      const t = peek();
      if ((t && t.t === 'id' && t.v === 'or') || isOp(t, '||')) { next(); l = { t: 'bin', op: 'or', l, r: parseAnd() }; }
      else return l;
    }
  }
  function parseAnd() {
    let l = parseNot();
    for (;;) {
      const t = peek();
      if ((t && t.t === 'id' && t.v === 'and') || isOp(t, '&&')) { next(); l = { t: 'bin', op: 'and', l, r: parseNot() }; }
      else return l;
    }
  }
  function parseNot() {
    const t = peek();
    if ((t && t.t === 'id' && t.v === 'not') || isOp(t, '!')) { next(); return { t: 'un', op: 'not', e: parseNot() }; }
    return parseCmp();
  }
  function parseCmp() {
    const l = parseAdd();
    const t = peek();
    if (t && t.t === 'op' && ['==', '!=', '>', '<', '>=', '<=', '='].includes(t.v)) {
      next();
      const op = t.v === '=' ? '==' : t.v;
      return { t: 'bin', op, l, r: parseAdd() };
    }
    return l;
  }
  function parseAdd() {
    let l = parseMul();
    for (;;) {
      const t = peek();
      if (isOp(t, '+') || isOp(t, '-')) { next(); l = { t: 'bin', op: t.v, l, r: parseMul() }; }
      else return l;
    }
  }
  function parseMul() {
    let l = parseUnary();
    for (;;) {
      const t = peek();
      if (isOp(t, '*') || isOp(t, '/') || isOp(t, '%')) { next(); l = { t: 'bin', op: t.v, l, r: parseUnary() }; }
      else return l;
    }
  }
  function parseUnary() {
    const t = peek();
    if (isOp(t, '-')) { next(); return { t: 'un', op: 'neg', e: parseUnary() }; }
    return parsePrimary();
  }
  function parsePrimary() {
    const t = next();
    if (!t) throw new Error('식이 불완전합니다');
    if (t.t === 'num') return { t: 'num', v: t.v };
    if (t.t === 'str') return { t: 'str', v: t.v };
    if (isOp(t, '(')) {
      const e = parseOr();
      expectOp(')');
      return e;
    }
    if (t.t === 'id') {
      if (t.v === 'true') return { t: 'bool', v: true };
      if (t.v === 'false') return { t: 'bool', v: false };
      // 함수 호출
      if (isOp(peek(), '(')) {
        next();
        const args = [];
        if (!isOp(peek(), ')')) {
          args.push(parseOr());
          while (isOp(peek(), ',')) { next(); args.push(parseOr()); }
        }
        expectOp(')');
        return { t: 'call', name: t.v, args };
      }
      throw new Error(`알 수 없는 이름: ${t.v} — 속성 참조는 prop("이름") 형태로 쓰세요`);
    }
    throw new Error(`예상치 못한 토큰: ${t.v}`);
  }

  const ast = parseOr();
  if (p < toks.length) throw new Error(`해석할 수 없는 부분: ${toks[p].v}`);
  return ast;
}

/* 파싱 결과 캐시 (수식 문자열 → AST/오류). 렌더마다 재파싱 방지 */
const astCache = new Map();
function cachedParse(src) {
  const key = String(src);
  if (astCache.has(key)) {
    const hit = astCache.get(key);
    if (hit.error) throw new Error(hit.error);
    return hit.ast;
  }
  try {
    const ast = parseFormula(key);
    if (astCache.size > 500) astCache.clear(); // 무한 성장 방지
    astCache.set(key, { ast });
    return ast;
  } catch (e) {
    if (astCache.size > 500) astCache.clear();
    astCache.set(key, { error: e.message || String(e) });
    throw e;
  }
}

/* ---------------- 값 유틸 ---------------- */
function truthy(v) {
  return !(v === false || v == null || v === '' || v === 0 || (Array.isArray(v) && v.length === 0));
}
function toNum(v) {
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const n = Number(v);
  if (v == null || v === '' || Number.isNaN(n)) throw new Error('숫자가 아닙니다');
  return n;
}
export function fmtDateVal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  const base = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) return base;
  return `${base} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toStr(v) {
  if (v == null) return '';
  if (v instanceof Date) return fmtDateVal(v);
  if (Array.isArray(v)) return v.map(toStr).join(', ');
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}
function toDateVal(v) {
  if (v instanceof Date) return v;
  if (typeof v === 'string' && v) {
    // YYYY-MM-DD 는 로컬 자정으로 (UTC 해석으로 하루 밀리는 것 방지)
    const s = /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v;
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  throw new Error('날짜가 아닙니다');
}
function looseEq(a, b) {
  if (typeof a === typeof b) return a === b;
  if (a instanceof Date || b instanceof Date) return toStr(a) === toStr(b);
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && a != null && b != null && !Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return toStr(a) === toStr(b);
}
function cmpVals(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date || b instanceof Date) return toDateVal(a) - toDateVal(b);
  const na = Number(a);
  const nb = Number(b);
  if (a !== '' && b !== '' && a != null && b != null && !Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  const sa = toStr(a);
  const sb = toStr(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

/* ---------------- 평가기 ---------------- */
function evalNode(n, env) {
  switch (n.t) {
    case 'num':
    case 'str':
    case 'bool':
      return n.v;
    case 'un':
      if (n.op === 'not') return !truthy(evalNode(n.e, env));
      return -toNum(evalNode(n.e, env)); // neg
    case 'bin': {
      if (n.op === 'and') return truthy(evalNode(n.l, env)) && truthy(evalNode(n.r, env));
      if (n.op === 'or') return truthy(evalNode(n.l, env)) || truthy(evalNode(n.r, env));
      const a = evalNode(n.l, env);
      const b = evalNode(n.r, env);
      switch (n.op) {
        case '+':
          if (typeof a === 'number' && typeof b === 'number') return a + b;
          return toStr(a) + toStr(b); // 문자열 연결
        case '-': return toNum(a) - toNum(b);
        case '*': return toNum(a) * toNum(b);
        case '/': {
          const d = toNum(b);
          if (d === 0) throw new Error('0으로 나눌 수 없습니다');
          return toNum(a) / d;
        }
        case '%': {
          const d = toNum(b);
          if (d === 0) throw new Error('0으로 나눌 수 없습니다');
          return toNum(a) % d;
        }
        case '==': return looseEq(a, b);
        case '!=': return !looseEq(a, b);
        case '>': return cmpVals(a, b) > 0;
        case '<': return cmpVals(a, b) < 0;
        case '>=': return cmpVals(a, b) >= 0;
        case '<=': return cmpVals(a, b) <= 0;
        default: throw new Error(`지원하지 않는 연산자: ${n.op}`);
      }
    }
    case 'call':
      return callFn(n.name, n.args, env);
    default:
      throw new Error('평가할 수 없는 식');
  }
}

const DATE_UNIT_MS = {
  days: 86400000, day: 86400000, '일': 86400000,
  hours: 3600000, hour: 3600000, '시간': 3600000,
  minutes: 60000, minute: 60000, '분': 60000,
};

function callFn(name, args, env) {
  const ev = (i) => evalNode(args[i], env);
  const need = (n) => {
    if (args.length < n) throw new Error(`${name}() 인자가 부족합니다 (${n}개 필요)`);
  };
  switch (name) {
    case 'prop': {
      need(1);
      return env.getProp(toStr(ev(0)));
    }
    case 'if':
      need(3);
      return truthy(ev(0)) ? ev(1) : ev(2);
    case 'concat':
      return args.map((_, i) => toStr(ev(i))).join('');
    case 'length': {
      need(1);
      const v = ev(0);
      return Array.isArray(v) ? v.length : toStr(v).length;
    }
    case 'round': {
      need(1);
      const v = toNum(ev(0));
      const digits = args.length > 1 ? toNum(ev(1)) : 0;
      const m = 10 ** digits;
      return Math.round(v * m) / m;
    }
    case 'abs':
      need(1);
      return Math.abs(toNum(ev(0)));
    case 'empty': {
      need(1);
      const v = ev(0);
      return v == null || v === '' || (Array.isArray(v) && v.length === 0);
    }
    case 'now':
      return new Date();
    case 'dateBetween': {
      need(3);
      const d1 = toDateVal(ev(0));
      const d2 = toDateVal(ev(1));
      const unit = toStr(ev(2));
      if (unit === 'months' || unit === 'month' || unit === '개월') {
        return (d1.getFullYear() - d2.getFullYear()) * 12 + (d1.getMonth() - d2.getMonth());
      }
      if (unit === 'years' || unit === 'year' || unit === '년') {
        return d1.getFullYear() - d2.getFullYear();
      }
      const ms = DATE_UNIT_MS[unit];
      if (!ms) throw new Error(`지원하지 않는 단위: ${unit} (days/hours/minutes/months/years)`);
      return Math.trunc((d1 - d2) / ms);
    }
    case 'format':
      need(1);
      return toStr(ev(0));
    default:
      throw new Error(`알 수 없는 함수: ${name}`);
  }
}

/* ---------------- 공개 API ----------------
   evaluateFormula(src, getProp)
   - getProp(name): 속성값 콜백 (formula→formula 재귀·깊이 제한은 호출부 담당)
   - 성공 시 값 반환, 실패 시 throw (message = 한국어 오류) */
export function evaluateFormula(src, getProp) {
  const ast = cachedParse(src);
  return evalNode(ast, { getProp });
}
